import React, {useMemo} from "react";
import {useForm} from "react-hook-form";
import {TextField} from "@components/Form/TextField";
import {AddToCartRequest, CatalogProductSpecificType, ConfirmCartRequest, ProductVm} from "@lib/Catalog/CatalogModels";
import ResponsivePicture from "@components/ResponsivePicture/ResponsivePicture";
import "./RedeemRequestModalContent.scss";
import {useUser} from "@lib/User/UserHooks";
import z from "zod";
import {TFunction} from "i18next";
import {zodResolver} from "@hookform/resolvers/zod";
import {useProjectTranslations} from "@i18n/i18nUtils";
import {formatPoints} from "@commons/Utils";
import CatalogService from "@lib/Catalog/CatalogService";
import {useAuthStore} from "@store/AuthStore";
import {useQueryClient} from "@tanstack/react-query";
import {invalidateAfterRedeem} from "@queries/invalidations";
import {ChevronRightIcon} from "@components/Icons/ChevronRightIcon";

type RedeemKind = "DIGITAL" | "PHYSICAL";

const DUMMY_DIGITAL_ADDRESSES: Record<string, Partial<ConfirmCartRequest["address"]>> = {
    // ILIADE, 3 Rue Christophe Colomb, 91300 Massy, France
    FR: {
        name: "Digital",
        surname: "Delivery",
        phone: "+33164868080",
        country: "FR",
        province: "Essonne",
        city: "Massy",
        postalCode: "91300",
        street: "3 Rue Christophe Colomb",
        careOf: "ILIADE",
    },
    // Șos. Pipera, Nr. 46D-46E-48, Oregon Park, Clădirea C, Etajul 5, Sectorul 2, București, România
    RO: {
        name: "Digital",
        surname: "Delivery",
        phone: "+40314370650",
        country: "RO",
        province: "Sectorul 2",
        city: "București",
        postalCode: "020309",
        street: "Șos. Pipera, Nr. 46D-46E-48, Oregon Park, Clădirea C, Etajul 5",
        careOf: "",
    }
};

export const makeRedeemRequestSchema = (t: TFunction, kind: RedeemKind) =>
    z
        .object({
            email: z
                .email(t("redemption:form:validation:email_required"))
                .min(1, t("redemption:form:validation:email_invalid")),

            name: z.string().optional(),
            surname: z.string().optional(),
            phone: z.string().optional(),

            state: z.string().optional(),
            city: z.string().optional(),
            zipcode: z.string().optional(),
            address: z.string().optional(),

            careOf: z.string().optional(),
        })
        .superRefine((data, ctx) => {
            if (kind === "DIGITAL") return;
            const required: Array<[keyof typeof data, string]> = [
                ["name", t("redemption:form:validation:name_required")],
                ["surname", t("redemption:form:validation:surname_required")],
                ["phone", t("redemption:form:validation:phone_required")],
                ["state", t("redemption:form:validation:state_required")],
                ["city", t("redemption:form:validation:city_required")],
                ["zipcode", t("redemption:form:validation:zipcode_required")],
                ["address", t("redemption:form:validation:address_required")],
            ];

            for (const [field, message] of required) {
                const v = String((data as any)[field] ?? "").trim();
                if (v.length === 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [field as string],
                        message,
                    });
                }
            }

        });

export type RedeemRequestFormValues = z.infer<ReturnType<typeof makeRedeemRequestSchema>>;

interface RedeemRequestModalContentProps {
    product: ProductVm;
    quantity: number;
    totalCost: number;
    onSuccessClick: () => void;
}

export default function RedeemRequestModalContent(props: RedeemRequestModalContentProps) {

    const {
        product,
        quantity,
        totalCost,
        onSuccessClick
    } = props;

    const queryClient = useQueryClient();

    const kind: RedeemKind = useMemo(() => {
        return product.productType === CatalogProductSpecificType.VOUCHER ? "DIGITAL" : "PHYSICAL";
    }, [product.productType]);

    const userCountry = useAuthStore(s => s.derivedCountry);

    const [error, setError] = React.useState<string | null>(null);
    const [success, setSuccess] = React.useState(false);

    const {data: user} = useUser();

    const {t} = useProjectTranslations();

    const schema = useMemo(() => makeRedeemRequestSchema(t, kind), [t, kind]);

    const {
        control,
        handleSubmit,
        formState: {isSubmitting},
    } = useForm<RedeemRequestFormValues>({
        mode: "onBlur",
        resolver: zodResolver(schema),
        defaultValues: {
            email: user?.email ?? "",
            name: user?.name ?? "",
            surname: user?.surname ?? "",
            phone: user?.phone ?? "",
            state: user?.customAttributes.find(ca => ca.hurlId === "address_state")?.values?.[0] ?? "",
            city: user?.customAttributes.find(ca => ca.hurlId === "address_city")?.values?.[0] ?? "",
            zipcode: user?.customAttributes.find(ca => ca.hurlId === "address_zipcode")?.values?.[0] ?? "",
            address: user?.customAttributes.find(ca => ca.hurlId === "adress_address")?.values?.[0] ?? "",
            careOf: "",
        },
    });

    const redeem = async (values: RedeemRequestFormValues) => {

        try {

            if (!product.redemptionModalityId) {
                throw new Error("Missing redemptionModalityId");
            }

            setError(null);


            /**
             * 1. Add to cart
             */
            const addToCartPayload: AddToCartRequest = {
                productLogicId: product.productLogicId,
                quantity: quantity,
                redemptionModalityLogicId: product.redemptionModalityId
            };

            await CatalogService.addToCart(addToCartPayload);

            /**
             * 2. Confirm cart
             */
            let confirmPayloadAddress: ConfirmCartRequest["address"] = {
                email: values.email.trim(),
            };

            if (kind === "PHYSICAL") {

                confirmPayloadAddress = {
                    ...confirmPayloadAddress,
                    name: values.name?.trim(),
                    surname: values.surname?.trim(),
                    phone: values.phone?.replace(/\s+/g, '').trim(),
                    country: userCountry?.toUpperCase() ?? "",
                    province: values.state?.trim(),
                    city: values.city?.trim(),
                    postalCode: values.zipcode?.trim(),
                    street: values.address?.trim(),
                    careOf: values.careOf?.trim() ?? "",
                }

            } else if (kind === "DIGITAL") {

                const currentCountry = userCountry?.toUpperCase() ?? "";

                const dummyAddress = DUMMY_DIGITAL_ADDRESSES[currentCountry];

                if (dummyAddress) {
                    confirmPayloadAddress = {
                        ...confirmPayloadAddress,
                        ...dummyAddress
                    };
                }
            }

            await CatalogService.confirmCart({
                address: confirmPayloadAddress
            });

            await invalidateAfterRedeem({
                queryClient,
                productId: product.productLogicId,
            });

            setSuccess(true);

        } catch (e) {
            console.log("Error submitting redemption request form:", e);
            setError(t("redemption:form:feedback:generic_error:message"));

            const cart = await CatalogService.getCart();

            if (cart && cart.items.length > 0) void CatalogService.removeCartItem(cart.items[0].catalogGroupProductLogicId);
        }

    }

    return (
        <div className="redeemModal">

            <div className="redeemModal__recap">
                {!!product.image &&
                    <ResponsivePicture images={[product.image]}/>
                }
                <div className="redeemModal__recapBody">
                    <div className="redeemModal__recapTitle tp-m semibold">{product.title}</div>

                    <div className="redeemModal__recapMeta tp-xs">
                        <span>{t("redemption:recap:labels:quantity", "Quantity")}: <strong>{quantity}</strong>
                        </span><span>{t("redemption:recap:labels:total", "Total")}: <strong>{formatPoints(totalCost)}</strong></span>
                    </div>
                </div>
            </div>

            {!success &&
                <form className="redeemModal__form" onSubmit={handleSubmit(redeem)}>

                    {kind === "DIGITAL" &&
                        <div className="redeemModal__grid">
                            <TextField
                                control={control}
                                name={"email"}
                                type={"email"}
                                label={t("redemption:form:labels:email")}
                                autoComplete={"email"}
                                disabled={isSubmitting}
                            />
                        </div>
                    }

                    {kind === "PHYSICAL" &&
                        <div>
                            <div className="redeemModal__grid">
                                <TextField
                                    control={control}
                                    name={"name"}
                                    label={t("redemption:form:labels:name")}
                                    disabled={isSubmitting}
                                />
                                <TextField
                                    control={control}
                                    name={"surname"}
                                    label={t("redemption:form:labels:surname")}
                                    disabled={isSubmitting}
                                />
                                <TextField
                                    control={control}
                                    name={"email"}
                                    type={"email"}
                                    label={t("redemption:form:labels:email")}
                                    autoComplete={"email"}
                                    disabled={isSubmitting}
                                />
                                <TextField
                                    control={control}
                                    name={"phone"}
                                    type={"tel"}
                                    label={t("redemption:form:labels:phone")}
                                    autoComplete={"tel"}
                                    inputMode={"tel"}
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div className="redeemModal__grid">
                                <div className="full redeemModal__grid__title">
                                    <h5 className="tp-s semibold">{t("redemption:form:sections:shipping:title")}</h5>
                                </div>

                                <TextField
                                    control={control}
                                    name={"state"}
                                    label={t("redemption:form:labels:state")}
                                    disabled={isSubmitting}
                                />
                                <TextField
                                    control={control}
                                    name={"city"}
                                    label={t("redemption:form:labels:city")}
                                    autoComplete={"address-level2"}
                                    disabled={isSubmitting}
                                />
                                <TextField
                                    control={control}
                                    name={"zipcode"}
                                    label={t("redemption:form:labels:zipcode")}
                                    autoComplete={"postal-code"}
                                    disabled={isSubmitting}
                                />

                                <TextField
                                    control={control}
                                    name={"address"}
                                    label={t("redemption:form:labels:address")}
                                    autoComplete={"address-line1"}
                                    disabled={isSubmitting}
                                />

                                <TextField
                                    control={control}
                                    name={"careOf"}
                                    label={t("redemption:form:labels:careOf")}
                                    disabled={isSubmitting}
                                    className={"full"}
                                />
                            </div>

                        </div>
                    }

                    <button className="cta cta--medium cta--primary redeemModal__submit" type="submit"
                            disabled={isSubmitting}>
                        <span
                            className="label">{isSubmitting ? t("redemption:form:cta:label:submitting") : t("redemption:form:cta:label")}</span>
                    </button>

                    {!!error &&
                        <p className="redeem_error tp-xs semibold color-primary">{error}</p>
                    }

                </form>
            }

            {success &&
                <div className={"redeem__success"}>
                    <p className="tp-s semibold color-primary" style={{marginBlock: 20}}>{t("redemption:form:feedback:success:message")}</p>
                    <button
                        onClick={onSuccessClick}
                        className={`cta cta--medium`}>
                        <span className="label">{t("redemption:form:feedback:success:history_cta:label")}</span>
                        <span className="icon">
                        <ChevronRightIcon/>
                    </span>
                    </button>
                </div>
            }

        </div>
    );
}